import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractWidgetComponent, ContextChange, DialogService, DictionaryEntry, MasterDetailListEntry, ResponseMessage, ResponseStatus, TranslationService, WidgetStyle } from '@sdbn/common';
import { Subscription } from 'rxjs';

import { FilterAdministrationContext } from '../filter-administration.context';
import { EditorFilter, EditorFilterTreeNode, Filter, FilterCondition, FilterMailSchedule, FilterSortOrder, LogicalOperator, PreviewFilter } from '../filter-administration.model';
import { FilterAdministrationService } from '../filter-administration.service';

const source = 'app-filter-administration';

@Component({
  selector: 'app-filter-administration',
  templateUrl: './filter-administration.component.html',
  styleUrls: ['./filter-administration.component.scss']
})
export class FilterAdministrationComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  newId = 0;

  filterAdministrationContextSubscription: Subscription;
  languageSubscription: Subscription;
  filterAdministrationContext = new FilterAdministrationContext();

  isLoading = false;

  previewFilters: PreviewFilter[] = [];
  selectedFilter: PreviewFilter;

  responseMessages = new Array<ResponseMessage>();

  constructor(
    public translationService: TranslationService,
    private filterAdministrationService: FilterAdministrationService,
    private dialog: MatDialog,
    private dialogService: DialogService
  ) {
    super();
    this.languageSubscription = this.translationService.changed.subscribe(language => {

      this.createTranslatedValuesOnList();

    });
    this.filterAdministrationContextSubscription = this.filterAdministrationContext.contextChange$.subscribe(
      change => {
        try {

        } catch (error) {
          console.error(error);
        }
      }
    );
  }

  ngOnInit(): void {
  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.filterAdministrationContextSubscription != null) {
      this.filterAdministrationContextSubscription.unsubscribe();
    }
    if (this.languageSubscription != null) {
      this.languageSubscription.unsubscribe();
    }
  }

  createTranslatedValuesOnList() {
    for (const supplementaryEquipment of this.previewFilters) {
      supplementaryEquipment.translatedName = this.translationService.translate(supplementaryEquipment.nameDictionaryKey);
    }
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
    this.filterAdministrationContext.setFilterType(this.widgetReference?.data?.filterType, source);
    this.loadFilters();
  }

  private loadFilters() {
    if (this.filterAdministrationContext.getFilterType() == null) {
      return;
    }

    this.previewFilters = new Array<PreviewFilter>();
    this.filterAdministrationService.getPreviewFilters(this.filterAdministrationContext.getFilterType()).subscribe(
      filters => {
        filters.forEach(
          f => {
            this.previewFilters.push(new PreviewFilter(f));
            this.previewFilters.sort((a, b) => {
              return a.sortOrder > b.sortOrder ? 1 : 0;
            });
          }
        );

        let selected = null;
        if (this.filterAdministrationContext.getFilter()?.id) {
          selected = this.previewFilters.find(pv => pv.id == this.filterAdministrationContext.getFilter()?.id)
        }

        if (selected == null) {
          selected = this.previewFilters[0];
        }

        if (this.previewFilters.length > 0) {
          this.performFilterSelection(selected);
        } else {
          this.addNew();
        }
        this.createTranslatedValuesOnList();
      }
    );
  }

  public setSelectedFilter(filter: PreviewFilter) {
    if (this.filterAdministrationContext.getFilter()?.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.filter-administration.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.filter-administration.discard.confirm-dialog.message'
      ).afterClosed().subscribe(() => {
        // clear current edit filter
        this.filterAdministrationContext.getFilter().dirty = false;
        this.performFilterSelection(filter);
      });

    } else {
      this.performFilterSelection(filter);
    }
  }

  private performFilterSelection(filter: PreviewFilter) {
    this.selectedFilter = filter;
    if (this.selectedFilter != null && this.selectedFilter.id >= 0) {
      this.filterAdministrationService.getFilter(this.selectedFilter.id).subscribe(
        result => {
          const editorFilter = new EditorFilter(result);
          // fallback für altdaten
          if (editorFilter.rootNode == null) {
            editorFilter.rootNode = new EditorFilterTreeNode({
              id: this.filterAdministrationContext.getNextTransientId(),
              operator: LogicalOperator.AND,
              conditions: [],
              nodes: []
            });
          }
          this.filterAdministrationContext.setFilter(editorFilter, source);
        }
      );
    }
  }

  public deleteFilter(filter: PreviewFilter) {

    this.dialogService.showConfirm(
      'tmc-yard.ui.admin.filter-administration.delete.confirm-dialog.title',
      'tmc-yard.ui.admin.filter-administration.delete.confirm-dialog.message'
    ).afterClosed().subscribe(() => {
      this.isLoading = true;
      this.filterAdministrationService.deleteFilter(filter.id).subscribe(
        response => {
          this.responseMessages = response.responseMessages;
          if (response.status === ResponseStatus.SUCCESS) {
            this.loadFilters();
          }
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
        }
      );
    });

  }

  public addNew() {

    if (this.filterAdministrationContext.getFilter()?.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.filter-administration.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.filter-administration.discard.confirm-dialog.message'
      ).afterClosed().subscribe(() => {
        this.startFilterCreation();
      });

    } else {
      this.startFilterCreation();
    }
  }

  private startFilterCreation() {
    const filter = this.createNewFilter();
    this.selectedFilter = new PreviewFilter({ id: filter.id, sortOrder: null, nameDictionaryKey: null });
    this.filterAdministrationContext.setFilter(filter, source);
  }

  private createNewFilter(): EditorFilter {
    const newFilterId = this.filterAdministrationContext.getNextTransientId();

    const rootNode = new EditorFilterTreeNode({
      id: this.filterAdministrationContext.getNextTransientId(),
      filterId: newFilterId,
      conditions: new Array<FilterCondition>(),
      operator: LogicalOperator.AND,
      nodes: new Array<EditorFilterTreeNode>()
    });

    const filter = new EditorFilter({
      name: new DictionaryEntry(),
      id: newFilterId,
      rootNode,
      filterType: this.filterAdministrationContext.getFilterType(),
      sortOrder: this.previewFilters.length + '',
      filterMailSchedules: new Array<FilterMailSchedule>()
    });
    return filter;
  }

  public drop(event) {
    moveItemInArray(this.previewFilters, event.previousIndex, event.currentIndex);
    let index = 0;
    this.previewFilters.forEach(f => f.sortOrder = '' + index++);

    const sortOrders = new Array<FilterSortOrder>();
    for (const p of this.previewFilters) {
      const order = new FilterSortOrder(p);
      sortOrders.push(order);
    }
    this.isLoading = true;
    this.filterAdministrationService.saveSortOrder(sortOrders).subscribe(
      result => {
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
      }
    );
  }

  public onCancelDetails(dirty: boolean) {


    this.dialogService.showConfirm(
      'tmc-yard.ui.admin.filter-administration.cancel.confirm-dialog.title',
      'tmc-yard.ui.admin.filter-administration.cancel.confirm-dialog.message'
    ).afterClosed().subscribe(() => {
      if (this.selectedFilter.id < 0) {
        if (this.filterAdministrationContext.getFilter()) {
          this.filterAdministrationContext.getFilter().dirty = false;
        }
        this.addNew();
      }
      else {
        this.filterAdministrationService.getFilter(this.selectedFilter.id).subscribe(
          result => {
            const editorFilter = new EditorFilter(result);
            // fallback für altdaten
            if (editorFilter.rootNode == null) {
              editorFilter.rootNode = new EditorFilterTreeNode({
                id: this.filterAdministrationContext.getNextTransientId(),
                operator: LogicalOperator.AND,
                conditions: [],
                nodes: []
              });
            }
            this.filterAdministrationContext.setFilter(editorFilter, source);
          }
        );
      }
    });
  }

  public onSaveDetails(filter: EditorFilter) {
    const saveFilter = new Filter(filter);

    if (saveFilter.id > 0) {
      const updatedSortOrder = this.previewFilters.find(p => p.id === saveFilter.id)?.sortOrder;
      if (updatedSortOrder != null) {
        saveFilter.sortOrder = updatedSortOrder;
      }
    }

    this.filterAdministrationService.saveFilter(saveFilter).subscribe(
      () => {
        if (this.filterAdministrationContext.getFilter()) {
          this.filterAdministrationContext.getFilter().dirty = false;
        }
        this.loadFilters();
      }
    );
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelectedFilter(entry as PreviewFilter);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.deleteFilter(entry as PreviewFilter);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
